/**
 * LiveBadge - Wave 0 (2026-05-27). Indicates a real-time data stream
 * (SSE notification feed, live findings, decision tap, etc.).
 *
 * Visual: 8px dot with a pulsing halo. Halo is paused under reduced
 * motion to satisfy WCAG 2.3.3 + battery-saving heuristics.
 */
import { useMotionLevel } from '@/lib/motion-level';
import { cn } from '@/lib/cn';

type LiveStatus = 'live' | 'connecting' | 'idle' | 'error';

interface LiveBadgeProps {
  status?: LiveStatus;
  label?: string;
  className?: string;
}

const COLORS: Record<LiveStatus, string> = {
  live: 'bg-success',
  connecting: 'bg-warning',
  idle: 'bg-muted-foreground',
  error: 'bg-destructive',
};

export function LiveBadge({
  status = 'live',
  label,
  className,
}: LiveBadgeProps): JSX.Element {
  const level = useMotionLevel();
  const pulse = level === 'full' && (status === 'live' || status === 'connecting');
  const colorClass = COLORS[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground',
        className,
      )}
    >
      <span className="relative inline-flex h-2 w-2">
        {pulse && (
          <span
            className={cn(
              'absolute inset-0 rounded-full opacity-60',
              colorClass,
              'animate-ping',
            )}
          />
        )}
        <span className={cn('relative inline-flex h-2 w-2 rounded-full', colorClass)} />
      </span>
      {label ?? status}
    </span>
  );
}
